/**
 * The segment builder demo's engine: made-up people, a field catalogue built
 * from whatever columns they have, derived fields, and rule evaluation — all
 * in the browser, with nothing sent anywhere.
 *
 * ## IT IS EXAMPLE DATA, GENERATED HERE
 *
 * 240 people are generated from a fixed seed by a small PRNG, so every reader
 * sees the same counts and the site suite can assert them. Names are drawn
 * from short lists of common Indian first names and surnames; emails end in
 * `example.test` (a reserved name that can never deliver) and are drawn masked
 * anyway, and no phone number exists at all — only "has a WhatsApp number",
 * yes or no. The section that renders this says, in its first line, that the
 * people are made up and generated in the browser (no "Example" tag: the
 * owner asked for those to go, Oct 2026). Dates are evaluated against a fixed day, {@link DEMO_TODAY}, stated
 * on screen, so "in the last 30 days" does not drift as the page ages.
 *
 * ## THE SAME VOCABULARY AS THE PRODUCT
 *
 * Field types, operator ids, which operators each type offers and the words
 * each operator reads as are the product's (`packages/shared/src/contracts/
 * rules.ts`: `OPERATORS_BY_TYPE`, `operatorLabel`). They are copied, not
 * imported — this page has no dependency on the product's contracts package
 * and should not grow one for a demo — and `scripts/verify-site-copy.tsx`
 * imports the real ones and fails if the copy drifts. Derived kinds are the
 * product's too (`years_since`, `days_since`, `month_of`), and the "Age from
 * <column>" suggestion uses the product's rule: offered, never created on its
 * own, for a date column whose name contains dob, birth or born.
 *
 * ## WHAT "ANY COLUMN" MEANS HERE
 *
 * The catalogue is not a list of known fields. It is built by reading the
 * upload's column names and detecting each column's type from its values, as
 * the product does on import: so "Joining date" is a date and "Loyalty tier"
 * is text because of what is in them, and a derived field can read any date
 * column, not only a birthday.
 */

export const DEMO_TODAY = '2026-10-18';
export const DEMO_TODAY_LABEL = '18 Oct 2026';
export const DEMO_PEOPLE_COUNT = 240;
export const DEMO_UPLOAD_NAME = 'Acme loyalty list';

export type FieldType = 'text' | 'number' | 'boolean' | 'date' | 'enum' | 'event';

export type RuleOperator =
  | 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'between'
  | 'in_last' | 'not_in_last' | 'before' | 'after' | 'anniversary_in_next'
  | 'is' | 'is_not' | 'contains' | 'any_of'
  | 'is_true' | 'is_false' | 'known' | 'unknown' | 'ever' | 'never';

/** A copy of the product's `OPERATORS_BY_TYPE`; the site suite compares it with the real one. */
export const OPERATORS_BY_TYPE: Readonly<Record<FieldType, readonly RuleOperator[]>> = {
  number: ['gt', 'gte', 'lt', 'lte', 'eq', 'between', 'known', 'unknown'],
  date: ['in_last', 'not_in_last', 'before', 'after', 'between', 'anniversary_in_next', 'known', 'unknown'],
  text: ['is', 'is_not', 'contains', 'any_of', 'known', 'unknown'],
  enum: ['is', 'is_not', 'any_of', 'known', 'unknown'],
  boolean: ['is_true', 'is_false', 'known', 'unknown'],
  event: ['in_last', 'not_in_last', 'ever', 'never'],
};

/** A copy of the product's `operatorLabel(op, type).label`; compared by the site suite. */
export function operatorLabel(op: RuleOperator, type: FieldType): string {
  switch (op) {
    case 'gt': return 'is greater than';
    case 'gte': return 'is at least';
    case 'lt': return 'is less than';
    case 'lte': return 'is at most';
    case 'eq': return 'equals';
    case 'between': return 'is between';
    case 'in_last': return type === 'event' ? 'happened in the last' : 'is in the last';
    case 'not_in_last': return type === 'event' ? 'has not happened in the last' : 'is not in the last';
    case 'before': return 'is before';
    case 'after': return 'is after';
    case 'anniversary_in_next': return 'comes round in the next';
    case 'is': return 'is';
    case 'is_not': return 'is not';
    case 'contains': return 'contains';
    case 'any_of': return 'is any of';
    case 'is_true': return 'is true';
    case 'is_false': return 'is false';
    case 'known': return 'is known';
    case 'unknown': return 'is unknown';
    case 'ever': return 'has ever happened';
    case 'never': return 'has never happened';
  }
}

const VALUELESS: readonly RuleOperator[] = ['known', 'unknown', 'is_true', 'is_false', 'ever', 'never'];
export const operatorNeedsValue = (op: RuleOperator): boolean => !VALUELESS.includes(op);

/* -------------------------------------------------------------------------- */
/* The example people                                                          */
/* -------------------------------------------------------------------------- */

/** One uploaded row: column name → the text the spreadsheet held ('' = empty). */
export type UploadRow = Readonly<Record<string, string>>;

export interface DemoPerson {
  readonly id: number;
  readonly name: string;
  /** Masked already, as the product shows an address in a list. */
  readonly maskedEmail: string | null;
  readonly hasWhatsApp: boolean;
  readonly source: 'upload' | 'reach_lead';
  readonly row: UploadRow;
  /** Activity Wend records itself: the last WhatsApp reply, or null. */
  readonly repliedAt: string | null;
}

/** mulberry32 — tiny, seeded, good enough to scatter example data. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const FIRST = ['Priya', 'Rahul', 'Ananya', 'Rohan', 'Sneha', 'Aditya', 'Pooja', 'Vikram', 'Neha', 'Arjun', 'Kavya', 'Siddharth', 'Meera', 'Karan', 'Isha', 'Nikhil', 'Divya', 'Amit', 'Shruti', 'Varun'];
const LAST = ['Shinde', 'Patil', 'Kulkarni', 'Deshmukh', 'Joshi', 'Pawar', 'Jadhav', 'Gokhale', 'Bhosale', 'Kale', 'More', 'Apte'];
const CITIES = ['Pune', 'Nashik', 'Mumbai', 'Nagpur', 'Aurangabad', 'Kolhapur'];
const TIERS = ['Silver', 'Gold', 'Platinum'];

const DAY_MS = 86_400_000;
const toUtc = (iso: string): number => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
const TODAY_UTC = toUtc(DEMO_TODAY);
const isoDaysAgo = (days: number): string => new Date(TODAY_UTC - days * DAY_MS).toISOString().slice(0, 10);

function generate(): readonly DemoPerson[] {
  const r = rng(71);
  const pick = <T,>(list: readonly T[]): T => list[Math.floor(r() * list.length)] as T;
  const people: DemoPerson[] = [];
  for (let id = 1; id <= DEMO_PEOPLE_COUNT; id += 1) {
    const first = pick(FIRST);
    const last = pick(LAST);
    const source = r() < 0.18 ? 'reach_lead' : 'upload';
    const lead = source === 'reach_lead';
    // A Reach lead carries a number and an ad, and none of the loyalty columns.
    const birthday = !lead && r() < 0.82 ? isoDaysAgo(Math.floor(365 * (19 + r() * 46) + r() * 365)) : '';
    const joining = !lead && r() < 0.9 ? isoDaysAgo(Math.floor(30 + r() * 2400)) : '';
    const orders = lead ? '' : String(Math.floor(r() * r() * 14));
    const lastOrder = !lead && orders !== '0' && r() < 0.95 ? isoDaysAgo(Math.floor(r() * 420)) : '';
    const row: UploadRow = {
      City: r() < 0.94 ? pick(CITIES) : '',
      Birthday: birthday,
      'Joining date': joining,
      'Loyalty tier': !lead && r() < 0.75 ? pick(TIERS) : '',
      Orders: orders,
      'Last order': lastOrder,
      'WhatsApp opt-in': lead ? 'yes' : r() < 0.7 ? 'yes' : r() < 0.6 ? 'no' : '',
    };
    const hasEmail = !lead && r() < 0.72;
    people.push({
      id,
      name: `${first} ${last}`,
      maskedEmail: hasEmail ? `${first.charAt(0).toLowerCase()}***@example.test` : null,
      hasWhatsApp: lead || r() < 0.88,
      source,
      row,
      repliedAt: r() < 0.34 ? isoDaysAgo(Math.floor(r() * 120)) : null,
    });
  }
  return people;
}

export const DEMO_PEOPLE: readonly DemoPerson[] = generate();

/** The upload's columns in the order the spreadsheet had them. */
export const UPLOAD_COLUMNS: readonly string[] = ['City', 'Birthday', 'Joining date', 'Loyalty tier', 'Orders', 'Last order', 'WhatsApp opt-in'];

/* -------------------------------------------------------------------------- */
/* The catalogue                                                               */
/* -------------------------------------------------------------------------- */

export type FieldGroup = 'profile' | 'columns' | 'derived' | 'activity';

export const FIELD_GROUP_LABEL: Readonly<Record<FieldGroup, string>> = {
  profile: 'Profile',
  columns: 'Your columns',
  derived: 'Derived',
  activity: 'Activity',
};

export type DerivedKind = 'years_since' | 'days_since' | 'month_of';

export const DERIVED_KIND_LABEL: Readonly<Record<DerivedKind, string>> = {
  years_since: 'Years since (e.g. Age from a birth date)',
  days_since: 'Days since',
  month_of: 'Month of',
};

export interface DerivedField {
  readonly key: string;
  readonly label: string;
  readonly kind: DerivedKind;
  /** An upload column holding dates. */
  readonly source: string;
}

export interface DemoField {
  readonly key: string;
  readonly label: string;
  readonly group: FieldGroup;
  readonly type: FieldType;
  /** For a picker of values: text columns with few distinct values, and enums. */
  readonly values: readonly string[] | null;
  /** Share of people with a value, 0–100. */
  readonly coverage: number;
  readonly note?: string;
  readonly read: (person: DemoPerson) => string | number | boolean | null;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Detect a column's type from its values, the way an import does. */
export function detectType(values: readonly string[]): FieldType {
  const filled = values.filter((value) => value.trim() !== '');
  if (filled.length === 0) return 'text';
  if (filled.every((value) => /^-?\d+(\.\d+)?$/.test(value))) return 'number';
  if (filled.every((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))) return 'date';
  if (filled.every((value) => /^(yes|no|true|false)$/i.test(value))) return 'boolean';
  return 'text';
}

/** dob, birth or born in the column's name — the product's own suggestion rule. */
export function looksLikeBirthDate(column: string): boolean {
  const normalised = column.toLowerCase().replace(/[^a-z]/g, '');
  return ['dob', 'birth', 'born'].some((hint) => normalised.includes(hint));
}

function wholeYearsSince(iso: string): number {
  const born = new Date(toUtc(iso));
  const today = new Date(TODAY_UTC);
  let years = today.getUTCFullYear() - born.getUTCFullYear();
  const beforeAnniversary =
    today.getUTCMonth() < born.getUTCMonth() || (today.getUTCMonth() === born.getUTCMonth() && today.getUTCDate() < born.getUTCDate());
  if (beforeAnniversary) years -= 1;
  return years;
}

function derive(kind: DerivedKind, iso: string): number | string {
  if (kind === 'years_since') return wholeYearsSince(iso);
  if (kind === 'days_since') return Math.round((TODAY_UTC - toUtc(iso)) / DAY_MS);
  return MONTHS[Number(iso.slice(5, 7)) - 1] ?? '';
}

const coverageOf = (read: DemoField['read']): number => {
  const known = DEMO_PEOPLE.filter((person) => {
    const value = read(person);
    return value !== null && value !== '';
  }).length;
  return Math.round((known / DEMO_PEOPLE.length) * 100);
};

function columnField(column: string): DemoField {
  const values = DEMO_PEOPLE.map((person) => person.row[column] ?? '');
  const type = detectType(values);
  const distinct = [...new Set(values.filter((value) => value !== ''))].sort();
  const read: DemoField['read'] = (person) => {
    const raw = person.row[column] ?? '';
    if (raw === '') return null;
    if (type === 'number') return Number(raw);
    if (type === 'boolean') return /^(yes|true)$/i.test(raw);
    return raw;
  };
  return {
    key: `extras.${column}`,
    label: column,
    group: 'columns',
    type,
    values: type === 'text' && distinct.length <= 8 ? distinct : null,
    coverage: coverageOf(read),
    read,
  };
}

export function derivedFieldFor(kind: DerivedKind, source: string): DerivedField {
  const label =
    kind === 'years_since' ? (looksLikeBirthDate(source) ? 'Age' : `Years since ${source}`) : kind === 'days_since' ? `Days since ${source}` : `Month of ${source}`;
  return { key: `derived:${kind}_${source.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`, label, kind, source };
}

function derivedCatalogueField(derived: DerivedField): DemoField {
  const read: DemoField['read'] = (person) => {
    const raw = person.row[derived.source] ?? '';
    return raw === '' ? null : derive(derived.kind, raw);
  };
  const what = derived.kind === 'years_since' ? 'whole years since' : derived.kind === 'days_since' ? 'days since' : 'the month of';
  return {
    key: derived.key,
    label: derived.label,
    group: 'derived',
    type: derived.kind === 'month_of' ? 'enum' : 'number',
    values: derived.kind === 'month_of' ? MONTHS : null,
    coverage: coverageOf(read),
    note: `${what} ${derived.source} · as of ${DEMO_TODAY_LABEL}`,
    read,
  };
}

const PROFILE_FIELDS: readonly DemoField[] = [
  { key: 'name', label: 'Name', group: 'profile', type: 'text', values: null, coverage: 100, read: (person) => person.name },
  {
    key: 'source',
    label: 'Source',
    group: 'profile',
    type: 'enum',
    values: ['upload', 'reach_lead'],
    coverage: 100,
    read: (person) => person.source,
  },
];

const ACTIVITY_FIELDS: readonly DemoField[] = [
  {
    key: 'replied_whatsapp',
    label: 'Replied on WhatsApp',
    group: 'activity',
    type: 'event',
    values: null,
    coverage: 100,
    note: 'recorded by Wend from replies to your messages',
    read: (person) => person.repliedAt,
  },
];

/** The whole catalogue: profile, every upload column, the derived fields the reader added, activity. */
export function buildCatalogue(derived: readonly DerivedField[]): readonly DemoField[] {
  return [...PROFILE_FIELDS, ...UPLOAD_COLUMNS.map(columnField), ...derived.map(derivedCatalogueField), ...ACTIVITY_FIELDS];
}

/** Date columns a derived field may read — any of them, not only a birthday. */
export const DATE_COLUMNS: readonly string[] = UPLOAD_COLUMNS.filter((column) => columnField(column).type === 'date');

/** The one-click suggestions: Age from each date column whose name looks like a birth date. */
export function ageSuggestions(existing: readonly DerivedField[]): readonly DerivedField[] {
  return DATE_COLUMNS.filter(looksLikeBirthDate)
    .map((column) => derivedFieldFor('years_since', column))
    .filter((field) => !existing.some((have) => have.key === field.key));
}

/* -------------------------------------------------------------------------- */
/* Rules                                                                       */
/* -------------------------------------------------------------------------- */

export type Window = { readonly amount: number | ''; readonly unit: 'days' | 'months' };

export type RuleValue =
  | null
  | number
  | ''
  | string
  | readonly string[]
  | readonly [number | '', number | '']
  | readonly [string, string]
  | Window
  | { readonly days: number | '' };

export interface DemoRule {
  readonly kind: 'rule';
  readonly id: string;
  readonly field: string;
  readonly op: RuleOperator;
  readonly value: RuleValue;
}

export interface DemoGroup {
  readonly kind: 'group';
  readonly id: string;
  readonly join: 'and' | 'or';
  readonly children: readonly (DemoRule | DemoGroup)[];
}

export type DemoNode = DemoRule | DemoGroup;

/** A starting value for an operator, so a new rule reads sensibly at once. */
export function defaultValue(field: DemoField, op: RuleOperator): RuleValue {
  if (!operatorNeedsValue(op)) return null;
  if (op === 'in_last' || op === 'not_in_last') return { amount: 30, unit: 'days' };
  if (op === 'anniversary_in_next') return { days: 30 };
  if (op === 'between') return field.type === 'date' ? [isoDaysAgo(365), DEMO_TODAY] : [25, 40];
  if (op === 'before' || op === 'after') return isoDaysAgo(365);
  if (op === 'any_of') return field.values !== null && field.values[0] !== undefined ? [field.values[0]] : [];
  if (field.type === 'number') return field.key.startsWith('derived:years_since') ? 25 : 2;
  if ((op === 'is' || op === 'is_not') && field.values !== null) return field.values[0] ?? '';
  return '';
}

const isWindow = (value: RuleValue): value is Window => typeof value === 'object' && value !== null && 'unit' in value;
const isDays = (value: RuleValue): value is { days: number | '' } => typeof value === 'object' && value !== null && 'days' in value;

/** A rule with a missing value is ignored (and says so), as the product's builder does. */
export function isComplete(rule: DemoRule): boolean {
  if (!operatorNeedsValue(rule.op)) return true;
  const value = rule.value;
  if (value === null || value === '') return false;
  if (isWindow(value)) return value.amount !== '' && value.amount >= 1;
  if (isDays(value)) return value.days !== '' && value.days >= 0;
  if (Array.isArray(value)) {
    if (rule.op === 'between') return value.length === 2 && value[0] !== '' && value[1] !== '';
    return value.length > 0;
  }
  return true;
}

function windowDays(window: Window): number {
  return window.unit === 'days' ? Number(window.amount) : Number(window.amount) * 30;
}

function daysAgo(iso: string): number {
  return Math.round((TODAY_UTC - toUtc(iso)) / DAY_MS);
}

function anniversaryWithin(iso: string, days: number): boolean {
  const month = Number(iso.slice(5, 7)) - 1;
  const day = Number(iso.slice(8, 10));
  const today = new Date(TODAY_UTC);
  for (const year of [today.getUTCFullYear(), today.getUTCFullYear() + 1]) {
    const next = Date.UTC(year, month, day);
    const ahead = Math.round((next - TODAY_UTC) / DAY_MS);
    if (ahead >= 0) return ahead <= days;
  }
  return false;
}

export function matchRule(rule: DemoRule, field: DemoField, person: DemoPerson): boolean {
  const raw = field.read(person);
  const known = raw !== null && raw !== '';
  const { op, value } = rule;
  if (op === 'known') return known;
  if (op === 'unknown') return !known;
  if (op === 'ever') return known;
  if (op === 'never') return !known;
  if (op === 'not_in_last' && field.type === 'event') return !known || (isWindow(value) && daysAgo(String(raw)) > windowDays(value));
  if (!known) return false;

  switch (op) {
    case 'is_true': return raw === true;
    case 'is_false': return raw === false;
    case 'gt': return Number(raw) > Number(value);
    case 'gte': return Number(raw) >= Number(value);
    case 'lt': return Number(raw) < Number(value);
    case 'lte': return Number(raw) <= Number(value);
    case 'eq': return Number(raw) === Number(value);
    case 'between': {
      if (!Array.isArray(value)) return false;
      const [low, high] = value as unknown as readonly [number | string, number | string];
      return field.type === 'date' ? String(raw) >= String(low) && String(raw) <= String(high) : Number(raw) >= Number(low) && Number(raw) <= Number(high);
    }
    case 'in_last': return isWindow(value) && daysAgo(String(raw)) >= 0 && daysAgo(String(raw)) <= windowDays(value);
    case 'not_in_last': return isWindow(value) && daysAgo(String(raw)) > windowDays(value);
    case 'before': return String(raw) < String(value);
    case 'after': return String(raw) > String(value);
    case 'anniversary_in_next': return isDays(value) && anniversaryWithin(String(raw), Number(value.days));
    case 'is': return String(raw).toLowerCase() === String(value).trim().toLowerCase();
    case 'is_not': return String(raw).toLowerCase() !== String(value).trim().toLowerCase();
    case 'contains': return String(raw).toLowerCase().includes(String(value).trim().toLowerCase());
    case 'any_of': return Array.isArray(value) && (value as readonly string[]).some((option) => option.toLowerCase() === String(raw).toLowerCase());
    default: return false;
  }
}

export type FieldIndex = ReadonlyMap<string, DemoField>;

/** Whether a node matches; incomplete rules and empty groups are ignored (match everyone). */
export function matchNode(node: DemoNode, index: FieldIndex, person: DemoPerson): boolean {
  if (node.kind === 'rule') {
    const field = index.get(node.field);
    if (field === undefined || !isComplete(node)) return true;
    return matchRule(node, field, person);
  }
  const live = node.children.filter((child) => child.kind === 'group' || (index.has(child.field) && isComplete(child)));
  if (live.length === 0) return true;
  return node.join === 'and' ? live.every((child) => matchNode(child, index, person)) : live.some((child) => matchNode(child, index, person));
}

export interface DemoCounts {
  readonly matched: readonly DemoPerson[];
  readonly email: number;
  readonly whatsapp: number;
  /** Per rule and per nested group, people who match it on its own. */
  readonly perNode: ReadonlyMap<string, number>;
}

function allNodes(node: DemoNode, out: DemoNode[] = []): DemoNode[] {
  out.push(node);
  if (node.kind === 'group') for (const child of node.children) allNodes(child, out);
  return out;
}

export function countRules(root: DemoGroup, catalogue: readonly DemoField[]): DemoCounts {
  const index: FieldIndex = new Map(catalogue.map((field) => [field.key, field]));
  const matched = DEMO_PEOPLE.filter((person) => matchNode(root, index, person));
  const perNode = new Map<string, number>();
  for (const node of allNodes(root)) {
    if (node === root) continue;
    perNode.set(node.id, DEMO_PEOPLE.filter((person) => matchNode(node, index, person)).length);
  }
  return {
    matched,
    email: matched.filter((person) => person.maskedEmail !== null).length,
    whatsapp: matched.filter((person) => person.hasWhatsApp).length,
    perNode,
  };
}

/** The rules the demo opens with — every operator kind the reader is likely to try first is one click away. */
export function initialRules(): DemoGroup {
  return {
    kind: 'group',
    id: 'root',
    join: 'and',
    children: [
      { kind: 'rule', id: 'r1', field: 'extras.City', op: 'any_of', value: ['Pune', 'Nashik', 'Mumbai'] },
      { kind: 'rule', id: 'r2', field: 'extras.Orders', op: 'gte', value: 1 },
      {
        kind: 'group',
        id: 'g1',
        join: 'or',
        children: [
          { kind: 'rule', id: 'r3', field: 'extras.Last order', op: 'in_last', value: { amount: 6, unit: 'months' } },
          { kind: 'rule', id: 'r4', field: 'replied_whatsapp', op: 'in_last', value: { amount: 60, unit: 'days' } },
        ],
      },
    ],
  };
}

/** An id for a new rule or group, unique within the tree. */
export function nextId(root: DemoGroup, prefix: 'r' | 'g'): string {
  const used = new Set(allNodes(root).map((node) => node.id));
  for (let n = 1; ; n += 1) if (!used.has(`${prefix}${n}`)) return `${prefix}${n}`;
}

export function updateNode(root: DemoGroup, id: string, change: (node: DemoNode) => DemoNode): DemoGroup {
  const walk = (node: DemoNode): DemoNode => {
    if (node.id === id) return change(node);
    if (node.kind === 'group') return { ...node, children: node.children.map(walk) };
    return node;
  };
  return walk(root) as DemoGroup;
}

export function removeNode(root: DemoGroup, id: string): DemoGroup {
  const walk = (node: DemoGroup): DemoGroup => ({
    ...node,
    children: node.children.filter((child) => child.id !== id).map((child) => (child.kind === 'group' ? walk(child) : child)),
  });
  return walk(root);
}

export const formatCount = (value: number): string => value.toLocaleString('en-IN');
